import { ClubAwardPlaces, givenClubAwards } from '@/components/club-awards'
import { HeroWinner } from '@/components/hero-award'
import { useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { PrinterIcon } from 'lucide-react'
import { useReportSummary, useReportResults, useReportAwards } from '@/api/hooks'
import { SelectShowNotice } from '@/components/current-show'
import { useCurrentShow } from '@/context/current-show'
import { ReportLayout } from '@/layouts/ReportLayout'
import { Button } from '@/components/ui/button'
import { triggerReportPrint } from '@/lib/print'
import { parseDateOnlyToLocal } from '@/lib/date'
import {
  buildDivisionWinners,
  formatClassDisplayName,
  formatDivisionDisplayName,
  getPlacementParts,
  orderPlacementColumns,
  renderAwardWinner,
  sortClasses,
  sortDivisions,
} from '@/lib/report-results'
import type { ReportAwardsOutput, ReportResultsOutput, ReportSummaryOutput } from '@schemas'

export default function ShowResultsReportPage() {
  const { currentShow } = useCurrentShow()
  const [searchParams] = useSearchParams()

  const showIdParam = searchParams.get('showId') ?? undefined
  const showNameParam = searchParams.get('showName') ?? undefined
  const showLocationParam = searchParams.get('showLocation') ?? undefined
  const showStartDateParam = searchParams.get('showStartDate') ?? undefined
  const showEndDateParam = searchParams.get('showEndDate') ?? undefined

  const showId = currentShow?.id ?? showIdParam
  const showName = currentShow?.name ?? showNameParam ?? 'Selected Show'
  const showLocation = currentShow?.location ?? showLocationParam ?? undefined
  const showStartDate = currentShow?.startDate ?? showStartDateParam ?? undefined
  const showEndDate = currentShow?.endDate ?? showEndDateParam ?? undefined

  const summaryQuery = useReportSummary(showId)
  const resultsQuery = useReportResults(showId)
  const awardsQuery = useReportAwards(showId)

  const isLoading = summaryQuery.isLoading || resultsQuery.isLoading || awardsQuery.isLoading
  const error = summaryQuery.error ?? resultsQuery.error ?? awardsQuery.error ?? null

  const summary: ReportSummaryOutput | undefined = summaryQuery.data
  const results: ReportResultsOutput | undefined = resultsQuery.data
  const awards: ReportAwardsOutput | undefined = awardsQuery.data

  const orderedDivisions = useMemo(() => sortDivisions(results?.divisions ?? []), [results])
  const divisionWinners = useMemo(() => buildDivisionWinners(awards, results), [awards, results])
  const clubAwards = givenClubAwards(awards, showId, currentShow)
  const showDivisionAwards = awards?.bestInDivisionEnabled !== false

  const preparedRange = useMemo(() => {
    if (!showStartDate || !showEndDate) return undefined
    const start = parseDateOnlyToLocal(showStartDate)
    const end = parseDateOnlyToLocal(showEndDate)
    if (!start || !end) return undefined
    const formatter = new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' })
    return `${formatter.format(start)} – ${formatter.format(end)}`
  }, [showEndDate, showStartDate])

  if (!showId) {
    return <SelectShowNotice description="Choose a show to print the Show Results Report." />
  }

  const header = (
    <div className="space-y-1">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-1">
          <p className="text-sm uppercase tracking-wide text-muted-foreground">
            Show Results Report
          </p>
          <h1 className="text-3xl font-semibold leading-tight">{showName}</h1>
          {showLocation ? (
            <p className="text-muted-foreground text-sm">
              {showLocation}
              {preparedRange ? ` • ${preparedRange}` : ''}
            </p>
          ) : preparedRange ? (
            <p className="text-muted-foreground text-sm">{preparedRange}</p>
          ) : null}
        </div>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="print-hidden"
          onClick={triggerReportPrint}
        >
          <PrinterIcon className="mr-2 h-4 w-4" aria-hidden="true" />
          Print Report
        </Button>
      </div>
    </div>
  )

  if (isLoading) {
    return (
      <ReportLayout header={header} className="show-results-report">
        <p className="text-muted-foreground text-base">Loading Show Results Report…</p>
      </ReportLayout>
    )
  }

  if (error) {
    return (
      <ReportLayout header={header} className="show-results-report">
        <p className="text-destructive text-base">
          {error.message || 'Unable to load report data.'}
        </p>
      </ReportLayout>
    )
  }

  if (!summary || !results || !awards) {
    return (
      <ReportLayout header={header} className="show-results-report">
        <p className="text-destructive text-base">Report data was not available.</p>
      </ReportLayout>
    )
  }

  return (
    <ReportLayout header={header} className="show-results-report">
      <section className="space-y-3">
        <dl className="grid gap-3 sm:grid-cols-3 text-base">
          <div className="flex flex-col rounded-lg border border-border/60 bg-muted/30 p-3">
            <dt className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Entrants
            </dt>
            <dd className="text-2xl font-semibold">{summary.entrants}</dd>
          </div>
          <div className="flex flex-col rounded-lg border border-border/60 bg-muted/30 p-3">
            <dt className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Entries
            </dt>
            <dd className="text-2xl font-semibold">{summary.entries}</dd>
          </div>
          <div className="flex flex-col rounded-lg border border-border/60 bg-muted/30 p-3">
            <dt className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Black Jar Entries
            </dt>
            <dd className="text-2xl font-semibold">{summary.blackJarEntries}</dd>
          </div>
        </dl>
      </section>

      <section className="space-y-4">
        {orderedDivisions.length === 0 ? (
          <p className="text-muted-foreground">No judging results have been recorded.</p>
        ) : (
          <div className="space-y-6">
            {orderedDivisions.map(division => (
              <article
                key={division.divisionId}
                className="space-y-3 break-inside-avoid division-section"
              >
                <header className="flex flex-col gap-1">
                  <h3 className="text-lg font-semibold report-layout__subheading division-heading">
                    {formatDivisionDisplayName(division.divisionName, division.divisionCode)}
                  </h3>
                </header>
                <div className="report-columns">
                  {sortClasses(division.classes).map(showClass => {
                    const placementColumns = orderPlacementColumns(showClass.placements, {
                      placeCount: showClass.placeCount,
                    })
                    const classDisplayName = formatClassDisplayName(
                      showClass.className,
                      showClass.classCode
                    )
                    return (
                      <article key={showClass.classId} className="report-columns__item">
                        <h4 className="report-columns__class-heading">
                          <span className="class-name">{classDisplayName}</span>
                        </h4>
                        {placementColumns.length === 0 ? (
                          <p className="text-sm text-muted-foreground">
                            No placements recorded for this class.
                          </p>
                        ) : (
                          <div className="report-columns__placements">
                            {placementColumns.map(column => {
                              const { base, suffix } = getPlacementParts(column.label)
                              const renderLabel = () => (
                                <span className="report-columns__placement-label">
                                  <span>{base}</span>
                                  {suffix ? <sup>{suffix}</sup> : null}
                                </span>
                              )
                              return (
                                <div key={column.label} className="report-columns__placement-group">
                                  {column.entries.length === 0 ? (
                                    <div
                                      className="report-columns__placement-row"
                                      key={`${column.label}-placeholder`}
                                    >
                                      {renderLabel()}{' '}
                                      <span
                                        className="report-columns__placement-separator"
                                        aria-hidden="true"
                                      >
                                        -
                                      </span>{' '}
                                      <span className="report-columns__placement-name">-</span>
                                    </div>
                                  ) : (
                                    column.entries.map((entry, entryIndex) => {
                                      const exhibitor =
                                        entry.exhibitorName?.trim() || 'Unknown exhibitor'
                                      const key = entry.exhibitId
                                        ? `${column.label}-${entry.exhibitId}`
                                        : `${column.label}-${entry.exhibitorId ?? exhibitor}-${entryIndex}`
                                      return (
                                        <div key={key} className="report-columns__placement-row">
                                          {renderLabel()}{' '}
                                          <span
                                            className="report-columns__placement-separator"
                                            aria-hidden="true"
                                          >
                                            -
                                          </span>{' '}
                                          <span className="report-columns__placement-name">
                                            {exhibitor}
                                          </span>
                                        </div>
                                      )
                                    })
                                  )}
                                </div>
                              )
                            })}
                          </div>
                        )}
                      </article>
                    )
                  })}
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      <section className="space-y-4">
        <h2 className="text-xl font-semibold report-layout__section-heading">
          Awards &amp; Highlights
        </h2>
        <div className="grid gap-4 md:grid-cols-2">
          {clubAwards.map(({ title, measure, award }) => (
            <div
              key={title}
              className="rounded-lg border border-border/60 bg-muted/20 p-4 space-y-2"
            >
              <h3 className="text-base font-semibold uppercase tracking-wide text-muted-foreground">
                {title}
              </h3>
              <div className="space-y-1 text-sm leading-relaxed">
                <ClubAwardPlaces award={award} measure={measure} />
              </div>
            </div>
          ))}

          <div className="rounded-lg border border-border/60 bg-muted/20 p-4 space-y-2">
            <h3 className="text-base font-semibold uppercase tracking-wide text-muted-foreground">
              Best in Show
            </h3>
            <p className="text-sm leading-relaxed">{renderAwardWinner(awards.bestInShow)}</p>
          </div>

          {(awards.reserveGrands ?? []).length > 0 && (
            <div className="rounded-lg border border-border/60 bg-muted/20 p-4 space-y-2">
              <h3 className="text-base font-semibold uppercase tracking-wide text-muted-foreground">
                Reserve Grand Champion
              </h3>
              {awards.reserveGrands?.map(winner => (
                <p key={winner.awardId} className="text-sm leading-relaxed">
                  {renderAwardWinner(winner)}
                </p>
              ))}
            </div>
          )}
          <HeroWinner state={awards?.heroAward} />
          {showDivisionAwards ? (
            <div className="rounded-lg border border-border/60 bg-muted/20 p-4 space-y-2 md:col-span-2">
              <h3 className="text-base font-semibold uppercase tracking-wide text-muted-foreground">
                Best in Division
              </h3>
              {divisionWinners.length === 0 ? (
                <p className="text-sm leading-relaxed">
                  No division champions have been identified.
                </p>
              ) : (
                <ul className="space-y-2 text-sm leading-relaxed">
                  {divisionWinners.map(item => {
                    const divisionDisplayName = formatDivisionDisplayName(
                      item.divisionName,
                      item.divisionCode
                    )
                    return (
                      <li key={item.divisionId}>
                        <span className="font-semibold">{divisionDisplayName}:</span>{' '}
                        {renderAwardWinner(item.winner)}
                      </li>
                    )
                  })}
                </ul>
              )}
            </div>
          ) : null}

          <div className="rounded-lg border border-border/60 bg-muted/20 p-4 space-y-2">
            <h3 className="text-base font-semibold uppercase tracking-wide text-muted-foreground">
              Sweepstakes
            </h3>
            <p className="text-sm leading-relaxed">{renderAwardWinner(awards.sweepstakes)}</p>
          </div>
        </div>
      </section>

      <footer className="text-sm text-muted-foreground">
        Prepared for {showName}
        {preparedRange ? ` • ${preparedRange}` : ''}
        {summary.generatedAt
          ? ` • Generated ${new Intl.DateTimeFormat(undefined, {
              dateStyle: 'medium',
              timeStyle: 'short',
            }).format(new Date(summary.generatedAt))}`
          : ''}
      </footer>
    </ReportLayout>
  )
}
